import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { getApiErrorMessage } from '../api';
import { useAuth } from '../hooks/useAuth';
import { uploadUrl } from '../utils/uploads';
import {
  GENDER_OPTIONS,
  EMPTY_CANDIDATE_FORM,
  validateCandidateForm,
  salaryHintInr,
  SALARY_CURRENT_LABEL,
  SALARY_EXPECTED_LABEL,
  digitsOnlyPhone
} from '../utils/candidateFormValidation';
import {
  buildCandidateSavePayload,
  staffCandidateFormFromApi,
  toCandidateApiBody
} from '../utils/candidatePayload';
import { mergeParsedIntoCandidateForm, parsedResumeSummary } from '../utils/applyParsedResume';
import { formatAgeBadge, formatExperienceBadge, computeExperienceYearsFromProfessional } from '../utils/candidateProfileUtils';
import { normalizeFieldText } from '../utils/textCase';
import ModalShell from '../components/ModalShell';
import CandidateHistoryModal from '../components/CandidateHistoryModal';
import { ActionMenu, ActionMenuItem } from '../components/ActionMenu';
import { ResumePreviewModal } from '../components/ResumePreviewFrame';
import MultiSelectFilter from '../components/MultiSelectFilter';
import QuickFilterGrid from '../components/QuickFilterGrid';
import ListFilterBar from '../components/ListFilterBar';
import FilterSearchField from '../components/FilterSearchField';
import { matchesOneOf } from '../utils/listFilters';
import SortableTh from '../components/SortableTh';
import { useSortableTable } from '../utils/sortableTable';
import { CANDIDATE_SORT_COLUMNS } from '../constants/listTableSort';
import SkillsPicker from '../components/SkillsPicker';
import ExpertiseAreasPicker from '../components/ExpertiseAreasPicker';
import CandidateAvatarWithCompletion from '../components/CandidateAvatarWithCompletion';
import WorkPreferencesFields from '../components/WorkPreferencesFields';
import {
  CANDIDATE_INPUT_CLASS,
  CANDIDATE_INPUT_ERROR_CLASS,
  candidateSectionClass,
  CANDIDATE_PROFILE_BADGE,
  CANDIDATE_MODAL_FOOTER_BTN,
  CANDIDATE_MODAL_PRIMARY_BTN,
  CANDIDATE_SELECT_CLASS,
  CORPORATE,
  CANDIDATE_FORM_MODAL_WIDTH,
  CANDIDATE_FORM_GRID,
  CANDIDATE_HEADING_EXPERTISE_GRID,
  CANDIDATE_FORM_ROW_GRID,
  CANDIDATE_EDUCATION_ROW_GRID,
  CANDIDATE_EDU_DEGREE_CELL,
  CANDIDATE_EDU_INSTITUTION_CELL,
  CANDIDATE_EDU_SMALL_CELL,
  CANDIDATE_SALARY_GRID
} from '../constants/candidateFormStyles';
import {
  PORTAL_LIST_PAGE,
  PORTAL_LIST_TABLE_SCROLL,
  PORTAL_TABLE_CARD_STICKY,
  PORTAL_CANDIDATES_TABLE,
  PORTAL_STICKY_ACTIONS_TH,
  PORTAL_STICKY_ACTIONS_TD
} from '../constants/portalUi';
import CandidateSectionHeader from '../components/CandidateSectionHeader';
import LanguagesKnownSection from '../components/LanguagesKnownSection';
import NoticePeriodField from '../components/NoticePeriodField';
import { normalizeLanguagesKnown } from '../utils/candidateLanguages';
import { mergeLocationCatalog } from '../utils/preferredWorkLocations';
import CandidateResumeSection from '../components/CandidateResumeSection';
import { EDUCATIONAL_QUALIFICATION_OPTIONS } from '../constants/educationOptions';
import { INDIA_STATES_AND_UTS, MARITAL_STATUS_OPTIONS } from '../constants/locationOptions';
import TableExportButton from '../components/TableExportButton';
import CandidateAdvancedFilterModal from '../components/CandidateAdvancedFilterModal';
import { candidateColumns, mapCandidates } from '../utils/dashboardColumns';
import CandidatePhoneLink from '../components/CandidatePhoneLink';
import {
  EMPTY_CANDIDATE_ADVANCED_FILTERS,
  EMPTY_CANDIDATE_FILTER_FACETS,
  buildCandidateFilterFacets,
  normalizeCandidateFilterFacets,
  countActiveAdvancedFilters,
  filterCandidatesList,
  hasActiveAdvancedFilters
} from '../utils/candidateFilters';

function fieldClass(error) {
  return error ? CANDIDATE_INPUT_ERROR_CLASS : CANDIDATE_INPUT_CLASS;
}

function FieldHint({ error, hint }) {
  if (error) return <p className="text-xs text-red-600 mt-1">{error}</p>;
  if (hint) return <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">{hint}</p>;
  return null;
}

export default function Candidates({
  embedded = false,
  embeddedEditId = null,
  initialCandidate = null,
  onEmbeddedClose = null
}) {
  const { user } = useAuth();
  const embeddedInitRef = useRef(false);
  const qc = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState([]);
  const [locationFilters, setLocationFilters] = useState([]);
  const [experienceFilters, setExperienceFilters] = useState([]);
  const [resumeFilters, setResumeFilters] = useState([]);
  const [skillsFilters, setSkillsFilters] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState({ ...EMPTY_CANDIDATE_ADVANCED_FILTERS });
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['candidates'],
    queryFn: () => api.get('/candidates').then(r => r.data)
  });

  const { data: filterFacetsApi, isLoading: filterFacetsLoading } = useQuery({
    queryKey: ['candidates-filter-facets'],
    queryFn: () => api.get('/candidates/filter-facets').then(r => r.data),
    staleTime: 30_000
  });

  /** Dropdown options from GET /candidates/filter-facets; falls back to list payload. */
  const filterFacets = useMemo(() => {
    if (filterFacetsApi && typeof filterFacetsApi === 'object') {
      return normalizeCandidateFilterFacets(filterFacetsApi);
    }
    return Array.isArray(data) ? buildCandidateFilterFacets(data) : EMPTY_CANDIDATE_FILTER_FACETS;
  }, [filterFacetsApi, data]);
  const facetsLoading = isLoading || filterFacetsLoading;

  const { data: skillsList } = useQuery({ queryKey: ['skills'], queryFn: () => api.get('/skills').then(r => r.data) });
  const { data: expertiseAreasList } = useQuery({
    queryKey: ['expertise-areas'],
    queryFn: () => api.get('/expertise-areas').then(r => r.data)
  });

  const refreshFilterFacets = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['candidates'] });
    qc.invalidateQueries({ queryKey: ['candidates-filter-facets'] });
  }, [qc]);

  const [form, setForm] = useState({ ...EMPTY_CANDIDATE_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showSkillsModal, setShowSkillsModal] = useState(null);
  const [resumePreviewSrc, setResumePreviewSrc] = useState(null);
  const [resumePreviewOpen, setResumePreviewOpen] = useState(false);
  const [listResumePreview, setListResumePreview] = useState(null);
  const [historyCandidate, setHistoryCandidate] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [parsingResume, setParsingResume] = useState(false);
  const [parseMessage, setParseMessage] = useState('');

  useEffect(() => {
    return () => {
      if (resumePreviewSrc?.startsWith('blob:')) URL.revokeObjectURL(resumePreviewSrc);
    };
  }, [resumePreviewSrc]);

  const clearResumePreviewSrc = () => {
    setResumePreviewSrc(prev => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const mutation = useMutation({
    mutationFn: (candidate) => api.put(`/candidates/${candidate.id}`, candidate).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
    }
  });

  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/candidates', payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
    }
  });

  const staffCanEditSkills = user?.role === 'admin' || user?.role === 'recruiter';

  const addSkillMutation = useMutation({
    mutationFn: skill => api.post('/skills', { skill }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['skills'] });
    }
  });

  const handleCreateSkill = async skill => {
    await addSkillMutation.mutateAsync(skill);
  };

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/candidates/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['jobs'] });
    }
  });

  const profileCompletionMailMutation = useMutation({
    mutationFn: id =>
      api
        .post('/candidates/send-profile-completion-email', { candidateId: id })
        .then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings-email-alerts'] });
    }
  });

  const sendProfileCompletionMail = async cand => {
    const email = cand.displayEmail || cand.contact?.email;
    if (!email) {
      window.alert('This candidate has no email address on file.');
      return;
    }
    if (
      !window.confirm(
        `Send profile completion email to ${email}? The message uses the template from Settings.`
      )
    ) {
      return;
    }
    try {
      await profileCompletionMailMutation.mutateAsync(cand.id);
      window.alert('Profile completion email sent. Check Email Alerts in Settings to track delivery.');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to send email';
      window.alert(msg);
    }
  };

  useEffect(() => {
    if (!showModal || !editing?.id || !data) return;
    const updated = data.find(c => c.id === editing.id);
    if (updated) setEditing(updated);
  }, [data, showModal, editing?.id]);

  const toggleWorkingMode = (mode) => {
    setForm(f => {
      const cur = f.preferredWorkingModes || [];
      const next = cur.includes(mode) ? cur.filter(m => m !== mode) : [...cur, mode];
      return { ...f, preferredWorkingModes: next };
    });
  };

  const toggleContactChannel = (ch) => {
    setForm(f => {
      const cur = f.contactChannels || [];
      const next = cur.includes(ch) ? cur.filter(c => c !== ch) : [...cur, ch];
      return { ...f, contactChannels: next };
    });
  };

  const closeStaffModal = () => {
    setShowModal(false);
    setEditing(null);
    setResumePreviewOpen(false);
    clearResumePreviewSrc();
    if (embedded && onEmbeddedClose) onEmbeddedClose();
  };

  const openModal = (cand = null) => {
    setResumeText('');
    setParseMessage('');
    setFormErrors({});
    clearResumePreviewSrc();
    setResumePreviewOpen(false);
    setEditing(cand);
    setForm(cand ? staffCandidateFormFromApi(cand) : { ...EMPTY_CANDIDATE_FORM });
    setShowModal(true);
  };

  useEffect(() => {
    if (!embedded || !embeddedEditId || embeddedInitRef.current) return;
    const cand =
      initialCandidate?.id === embeddedEditId
        ? initialCandidate
        : data?.find(c => c.id === embeddedEditId);
    if (cand) {
      embeddedInitRef.current = true;
      openModal(cand);
    }
  }, [embedded, embeddedEditId, initialCandidate, data]);

  const addEducation = () => {
    setForm(f => ({ ...f, educational: [...(f.educational || []), { degree: '', institution: '', year: '', field: '', gpa: '' }] }));
  };

  const fieldTextKind = field => {
    if (field === 'year' || field === 'gpa') return 'numeric';
    if (field === 'startDate' || field === 'endDate') return 'date';
    if (field === 'description') return 'text';
    return 'name';
  };

  const updateEducation = (index, field, value) => {
    const normalized =
      field === 'degree' ? value : normalizeFieldText(value, fieldTextKind(field));
    setForm(f => {
      const newEdu = [...(f.educational || [])];
      newEdu[index] = { ...newEdu[index], [field]: normalized };
      return { ...f, educational: newEdu };
    });
  };

  const removeEducation = (index) => {
    setForm(f => ({ ...f, educational: (f.educational || []).filter((_, i) => i !== index) }));
  };

  const addProfession = () => {
    setForm(f => ({ ...f, professional: [...(f.professional || []), { currentCompany: '', designation: '', startDate: '', endDate: '', description: '' }] }));
  };

  const updateProfession = (index, field, value) => {
    const normalized =
      field === 'startDate' || field === 'endDate'
        ? value
        : normalizeFieldText(value, fieldTextKind(field));
    setForm(f => {
      const newProf = [...(f.professional || [])];
      newProf[index] = { ...newProf[index], [field]: normalized };
      const expYears = f.isFresher
        ? '0'
        : (() => {
            const computed = computeExperienceYearsFromProfessional(newProf);
            return computed != null ? String(computed) : f.experience ?? '';
          })();
      return { ...f, professional: newProf, experience: expYears };
    });
  };

  const removeProfession = (index) => {
    setForm(f => ({ ...f, professional: (f.professional || []).filter((_, i) => i !== index) }));
  };

  const applyParsedResume = parsed => {
    setForm(f => ({
      ...mergeParsedIntoCandidateForm(f, parsed),
      resumeText: parsed.rawText || f.resumeText || ''
    }));
  };

  const parseResume = async () => {
    setParseMessage('');
    setParsingResume(true);
    try {
      let response;

      if (form.resumeFile) {
        const fd = new FormData();
        fd.append('resume', form.resumeFile);
        if (resumeText.trim()) fd.append('text', resumeText);
        response = await api.post('/resume/parse', fd);
      } else if (resumeText.trim()) {
        response = await api.post('/resume/parse', { text: resumeText });
      } else if (editing?.resumeLink) {
        const url = uploadUrl(editing.resumeLink);
        const res = await fetch(url);
        if (!res.ok) throw new Error('Could not load saved resume file');
        const blob = await res.blob();
        const ext = editing.resumeLink.split('.').pop() || 'pdf';
        const fd = new FormData();
        fd.append('resume', blob, `resume.${ext}`);
        response = await api.post('/resume/parse', fd);
      } else {
        setParseMessage('Upload a resume (PDF/DOCX/DOC/TXT/RTF/WPS) first.');
        return;
      }

      const parsed = response.data;
      if (parsed.rawText) setResumeText(parsed.rawText);
      applyParsedResume(parsed);
      setParseMessage(parsedResumeSummary(parsed));
    } catch (err) {
      if (err.response?.status === 404) {
        setParseMessage('Parse API not available. Restart the API server: cd server → npm run dev');
      } else {
        setParseMessage(err.response?.data?.message || err.message || 'Failed to parse resume');
      }
    } finally {
      setParsingResume(false);
    }
  };

  const submitForm = async () => {
    const { valid, errors } = validateCandidateForm(form, {
      editing: !!editing,
      mode: 'staff'
    });
    setFormErrors(errors);
    if (!valid) {
      const first = Object.values(errors)[0];
      if (first) alert(first);
      return;
    }

    const base = toCandidateApiBody(form);
    if (editing) {
      try {
        const payload = buildCandidateSavePayload({ ...editing, ...base }) || { ...editing, ...base };
        await mutation.mutateAsync(payload);
        const id = editing.id;
        if (form.photoFile && id) {
          const photoFormData = new FormData();
          photoFormData.append('photo', form.photoFile);
          const photoRes = await api.post(`/candidates/${id}/photo`, photoFormData);
          setEditing(prev => (prev ? { ...prev, photo: photoRes.data.photo } : prev));
        }
        if (form.resumeFile && id) {
          const resumeFormData = new FormData();
          resumeFormData.append('resume', form.resumeFile);
          const resumeRes = await api.post(`/candidates/${id}/resume`, resumeFormData);
          setEditing(prev =>
            prev
              ? {
                  ...prev,
                  resumeLink: resumeRes.data.resumeLink,
                  resumeOriginalName: resumeRes.data.resumeOriginalName || form.resumeFile?.name
                }
              : prev
          );
          setForm(f => ({
            ...f,
            resumeLink: resumeRes.data.resumeLink,
            resumeOriginalName: resumeRes.data.resumeOriginalName || f.resumeFile?.name || '',
            resumeFile: null
          }));
          clearResumePreviewSrc();
        }
        qc.invalidateQueries({ queryKey: ['candidates'] });
        qc.invalidateQueries({ queryKey: ['candidate', editing.id] });
        if (embedded) {
          closeStaffModal();
        } else {
          setShowModal(false);
          setEditing(null);
          setResumePreviewOpen(false);
          setForm({ ...EMPTY_CANDIDATE_FORM });
        }
      } catch (error) {
        console.error('Error updating candidate:', error);
        alert(error.response?.data?.message || 'Failed to save candidate');
      }
    } else {
      try {
        const result = await createMutation.mutateAsync(
          toCandidateApiBody(form, { includePassword: true })
        );
        const candidateId = result?.candidate?.id || result?.id;
        if (!candidateId) {
          throw new Error('Candidate was created but no ID was returned');
        }
        if (form.photoFile) {
          const photoFormData = new FormData();
          photoFormData.append('photo', form.photoFile);
          await api.post(`/candidates/${candidateId}/photo`, photoFormData);
        }
        if (form.resumeFile) {
          const resumeFormData = new FormData();
          resumeFormData.append('resume', form.resumeFile);
          await api.post(`/candidates/${candidateId}/resume`, resumeFormData);
        }
        qc.invalidateQueries({ queryKey: ['candidates'] });
        setShowModal(false);
        setEditing(null);
        setResumePreviewOpen(false);
        clearResumePreviewSrc();
        setResumeText('');
        setForm({ ...EMPTY_CANDIDATE_FORM });
      } catch (error) {
        console.error('Error creating candidate:', error);
        alert(getApiErrorMessage(error, 'Failed to create candidate'));
      }
    }
  };

  const getExperienceYears = (c) => {
    if (c.experienceYears != null && c.experienceYears !== '') {
      const n = Number(c.experienceYears);
      if (Number.isFinite(n)) return n;
    }
    if (c.experience != null && c.experience !== '') {
      const n = Number(c.experience);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };

  const candidateHasResume = (c) =>
    Boolean(c.hasResume || c.resumeLink || c.resume);

  const filterOptions = useMemo(
    () => ({
      locations: (filterFacets.locations || []).map(loc => {
        const str = String(loc).trim();
        return { value: str, label: str };
      })
    }),
    [filterFacets]
  );

  const locationCatalog = useMemo(
    () => mergeLocationCatalog(filterFacets.locations, form.preferredWorkLocations),
    [filterFacets.locations, form.preferredWorkLocations]
  );

  const filteredCandidates = useMemo(
    () =>
      filterCandidatesList(data || [], {
        searchQuery,
        statusFilters,
        locationFilters,
        experienceFilters,
        resumeFilters,
        skillsFilters,
        advancedFilters,
        getExperienceYears,
        candidateHasResume,
        expertiseCatalog: expertiseAreasList || []
      }),
    [
      data,
      searchQuery,
      statusFilters,
      locationFilters,
      expertiseAreasList,
      experienceFilters,
      resumeFilters,
      skillsFilters,
      advancedFilters
    ]
  );

  const { sort, toggleSort, sortedRows: sortedCandidates } = useSortableTable(
    filteredCandidates,
    CANDIDATE_SORT_COLUMNS
  );

  const candidatePoolForAdvanced = useMemo(
    () =>
      filterCandidatesList(data || [], {
        searchQuery,
        statusFilters,
        locationFilters,
        experienceFilters,
        resumeFilters,
        skillsFilters,
        advancedFilters: EMPTY_CANDIDATE_ADVANCED_FILTERS,
        getExperienceYears,
        candidateHasResume,
        expertiseCatalog: expertiseAreasList || []
      }),
    [
      data,
      searchQuery,
      statusFilters,
      locationFilters,
      expertiseAreasList,
      experienceFilters,
      resumeFilters,
      skillsFilters
    ]
  );

  const hasActiveFilters = Boolean(
    searchQuery.trim() ||
    statusFilters.length > 0 ||
    locationFilters.length > 0 ||
    experienceFilters.length > 0 ||
    resumeFilters.length > 0 ||
    skillsFilters.length > 0 ||
    hasActiveAdvancedFilters(advancedFilters)
  );

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilters([]);
    setLocationFilters([]);
    setExperienceFilters([]);
    setResumeFilters([]);
    setSkillsFilters([]);
    setAdvancedFilters({ ...EMPTY_CANDIDATE_ADVANCED_FILTERS });
  };

  const statusFilterOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const experienceFilterOptions = [
    { value: 'none', label: 'Not specified' },
    { value: '0-2', label: '0–2 years' },
    { value: '3-5', label: '3–5 years' },
    { value: '6-10', label: '6–10 years' },
    { value: '10+', label: '10+ years' }
  ];

  const resumeFilterOptions = [
    { value: 'yes', label: 'Has resume' },
    { value: 'no', label: 'No resume' }
  ];

  const skillsFilterOptions = [
    { value: 'has', label: 'Has core competencies' },
    { value: 'none', label: 'No core competencies' }
  ];

  if (!embedded && isLoading) return <div>Loading...</div>;

  const staffListLayout = !embedded;

  return (
    <div
      className={
        embedded
          ? ''
          : staffListLayout
            ? `${PORTAL_LIST_PAGE} candidates-list-page`
            : 'space-y-4 candidates-list-page'
      }
    >
      {!embedded && (
        <>
      <div className="flex-shrink-0 flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold">Candidates</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-xl transition-colors ${
              hasActiveAdvancedFilters(advancedFilters)
                ? 'border-[hsl(var(--ds-primary)/0.35)] text-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.06)] hover:bg-[hsl(var(--ds-primary)/0.12)]'
                : 'border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]'
            }`}
            title="Filter candidates"
            onClick={() => {
              refreshFilterFacets();
              setShowAdvancedFilter(true);
            }}
          >
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 2v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            Filter
          </button>
          <TableExportButton
            filename="candidates"
            sheetName="Candidates"
            columns={candidateColumns}
            rows={mapCandidates(sortedCandidates)}
          />
          <button
            type="button"
            className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-3 py-2 text-sm font-medium rounded-xl shadow-sm"
            onClick={() => openModal()}
          >
            Add candidate
          </button>
        </div>
      </div>

      <div className="flex-shrink-0">
      <ListFilterBar>
        <QuickFilterGrid
          search={
            <FilterSearchField
              placeholder="Search entire profile — name, skills, education, resume text (PDF/Word)…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery('')}
            />
          }
          filters={[
            <MultiSelectFilter
              key="status"
              options={statusFilterOptions}
              values={statusFilters}
              onChange={setStatusFilters}
              emptyLabel="All statuses"
            />,
            <MultiSelectFilter
              key="location"
              options={filterOptions.locations.map(loc => ({ value: loc, label: loc }))}
              values={locationFilters}
              onChange={setLocationFilters}
              emptyLabel="All locations"
            />,
            <MultiSelectFilter
              key="experience"
              options={experienceFilterOptions}
              values={experienceFilters}
              onChange={setExperienceFilters}
              emptyLabel="All experience"
            />,
            <MultiSelectFilter
              key="resume"
              options={resumeFilterOptions}
              values={resumeFilters}
              onChange={setResumeFilters}
              emptyLabel="All resumes"
            />,
            <MultiSelectFilter
              key="skills"
              options={skillsFilterOptions}
              values={skillsFilters}
              onChange={setSkillsFilters}
              emptyLabel="All competencies"
            />
          ]}
        />
        {hasActiveFilters && (
          <div className="mt-2 flex items-center justify-between text-xs text-[hsl(var(--ds-muted-fg))]">
            <span>
              Showing {sortedCandidates.length} of {data?.length || 0} candidates
            </span>
            <button type="button" className="text-[hsl(var(--ds-primary))] hover:underline" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}
      </ListFilterBar>
      </div>

      </>
      )}

      {!embedded && (
      <div className={`${PORTAL_TABLE_CARD_STICKY} flex-1 min-h-0 flex flex-col min-w-0`}>
        <div className={PORTAL_LIST_TABLE_SCROLL}>
          {(!data || data.length === 0) ? (
            <div className="px-4 py-8 text-center text-[hsl(var(--ds-muted-fg))]">No candidates found</div>
          ) : filteredCandidates.length === 0 ? (
            <div className="px-4 py-8 text-center text-[hsl(var(--ds-muted-fg))]">
              No candidates match your search or filters
            </div>
          ) : (
          <table className={PORTAL_CANDIDATES_TABLE}>
            <thead className="bg-[hsl(var(--ds-muted))] border-b border-[hsl(var(--ds-border))] sticky top-0 z-30 shadow-[0_1px_0_0_rgb(226,232,240)]">
            <tr>
              <th className="sticky-cand-sl text-left px-4 py-2 whitespace-nowrap">Sl No</th>
              <SortableTh label="Candidate ID" sortKey="id" sort={sort} onSort={toggleSort} className="sticky-cand-id px-4 py-2 whitespace-nowrap" />
              <SortableTh label="Candidate" sortKey="name" sort={sort} onSort={toggleSort} className="sticky-cand-name px-4 py-2 whitespace-nowrap" />
              <SortableTh label="Mobile" sortKey="mobile" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
              <SortableTh label="Email" sortKey="email" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
              <SortableTh label="Location" sortKey="location" sort={sort} onSort={toggleSort} className="col-cand-location px-4 py-2" />
              <SortableTh label="Experience" sortKey="experience" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
              <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
              <SortableTh label="Core Competencies" sortKey="skills" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
              <th className="text-left px-4 py-2 whitespace-nowrap">Resume</th>
              <th className={PORTAL_STICKY_ACTIONS_TH}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedCandidates.map((c, idx) => (
              <tr key={c.id} className="group border-b border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]">
                <td className="sticky-cand-sl sticky-portal-scroll px-4 py-2 whitespace-nowrap text-xs text-[hsl(var(--ds-muted-fg))] align-middle">{idx + 1}</td>
                <td className="sticky-cand-id sticky-portal-scroll px-4 py-2 whitespace-nowrap align-middle">
                  <span className="text-xs text-[hsl(var(--ds-muted-fg))]">{c.id}</span>
                </td>
                <td className="sticky-cand-name sticky-portal-scroll px-4 py-2 whitespace-nowrap align-middle">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="relative z-0 shrink-0">
                      <CandidateAvatarWithCompletion
                        candidate={c}
                        size="sm"
                        rounded="rounded-full"
                        title={c.displayName || c.personal?.name || c.contact?.name}
                      />
                    </div>
                    <div className="min-w-0 flex flex-col">
                      <button
                        type="button"
                        className="font-semibold truncate text-left text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:underline underline-offset-2"
                        title={c.displayName || c.personal?.name || c.contact?.name}
                        onClick={() => openModal(c)}
                      >
                        {c.displayName || c.personal?.name || c.contact?.name || '—'}
                      </button>
                      <span className="text-[10px] text-[hsl(var(--ds-muted-fg))] tabular-nums">
                        Profile {c.profileCompletionPercent ?? 0}% complete
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap align-middle">
                  <CandidatePhoneLink candidate={c} />
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-[hsl(var(--ds-muted-fg))] align-middle max-w-[12rem] truncate" title={c.displayEmail}>
                  {c.displayEmail || c.contact?.email || '—'}
                </td>
                <td className="col-cand-location px-4 py-2 align-middle" title={c.displayLocation}>
                  {c.displayLocation || '—'}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">{c.displayExperience || '—'}</td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-xl border ${c.status === 'inactive' ? 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))]' : 'bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))] border-[hsl(var(--ds-success)/0.25)]'}`}>
                    {c.status || 'active'}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {(c.skillCount ?? (c.skills || []).length) > 0 ? (
                    <button
                      type="button"
                      className="text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] text-xs"
                      onClick={() => setShowSkillsModal(c)}
                      title="View skills"
                    >
                      {(c.skillCount ?? c.skills.length)} skill{(c.skillCount ?? c.skills.length) === 1 ? '' : 's'} · View
                    </button>
                  ) : (
                    <span className="text-xs text-[hsl(var(--ds-muted-fg))]">—</span>
                  )}
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {candidateHasResume(c) && (
                    <button
                      type="button"
                      className="text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))]"
                      onClick={() =>
                        setListResumePreview({
                          src: c.resumeLink || c.resume || null,
                          fileName: c.resumeFileName || c.resumeOriginalName || '',
                          candidateName:
                            c.displayName || c.personal?.name || c.contact?.name || '',
                          candidateId: c.id
                        })
                      }
                      title="View Resume"
                    >
                      <span className="inline-flex items-center justify-center w-7 h-7 hover:bg-[hsl(var(--ds-primary)/0.08)] rounded-lg">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </span>
                    </button>
                  )}
                </td>
                <td className={PORTAL_STICKY_ACTIONS_TD}>
                  <ActionMenu>
                    <ActionMenuItem onClick={() => openModal(c)}>Edit</ActionMenuItem>
                    {(user?.role === 'admin' || user?.role === 'recruiter') && (
                      <ActionMenuItem
                        onClick={() =>
                          setHistoryCandidate({
                            id: c.id,
                            name:
                              c.displayName ||
                              c.personal?.name ||
                              c.contact?.name ||
                              c.contact?.email ||
                              c.id
                          })
                        }
                      >
                        Candidate history
                      </ActionMenuItem>
                    )}
                    <ActionMenuItem
                      disabled={profileCompletionMailMutation.isPending}
                      onClick={() => sendProfileCompletionMail(c)}
                    >
                      Send profile completion mail
                    </ActionMenuItem>
                    <ActionMenuItem onClick={() => mutation.mutate({ ...c, status: 'inactive' })}>Deactivate</ActionMenuItem>
                    <ActionMenuItem
                      danger
                      onClick={() => {
                        if (
                          window.confirm(
                            'Delete this candidate profile? All related ATS submissions for this candidate will also be removed.'
                          )
                        ) {
                          deleteMutation.mutate(c.id);
                        }
                      }}
                    >
                      Delete
                    </ActionMenuItem>
                  </ActionMenu>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
          )}
        </div>
      </div>
      )}

      <CandidateAdvancedFilterModal
        open={showAdvancedFilter}
        filters={advancedFilters}
        facets={filterFacets}
        facetsLoading={facetsLoading}
        candidatePool={candidatePoolForAdvanced}
        totalCandidates={data?.length || 0}
        getExperienceYears={getExperienceYears}
        expertiseCatalog={expertiseAreasList || []}
        onRefreshFacets={refreshFilterFacets}
        onClose={() => setShowAdvancedFilter(false)}
        onApply={next => {
          setAdvancedFilters(next);
          setShowAdvancedFilter(false);
        }}
      />

      {showModal && (
        <ModalShell
          title={
            editing ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                <span>{`Edit candidate — ${form.personal?.name || form.name || '—'} — ${form.id || editing.id || '—'}`}</span>
                <span className={`${CANDIDATE_PROFILE_BADGE} border-[hsl(var(--ds-primary)/0.25)] bg-[hsl(var(--ds-primary)/0.06)] text-[hsl(var(--ds-primary))]`}>
                  Age {formatAgeBadge(form.personal?.dob)}
                </span>
                <span className={`${CANDIDATE_PROFILE_BADGE} border-sky-200 bg-sky-50 text-sky-800`}>
                  {formatExperienceBadge(form.professional, form.experience)}
                </span>
              </span>
            ) : (
              'Add candidate'
            )
          }
          onClose={embedded ? closeStaffModal : () => setShowModal(false)}
          maxWidth={CANDIDATE_FORM_MODAL_WIDTH}
          corporate
          zIndex={embedded ? 'z-[14000]' : undefined}
          headerActions={
            editing?.id &&
            (user?.role === 'admin' || user?.role === 'recruiter') ? (
              <button
                type="button"
                className="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-xl border border-amber-300 bg-[hsl(var(--ds-warning)/0.15)] text-[hsl(var(--ds-warning-fg))] hover:bg-amber-100 transition-colors"
                onClick={() =>
                  setHistoryCandidate({
                    id: editing.id,
                    name:
                      editing.displayName ||
                      editing.personal?.name ||
                      editing.contact?.name ||
                      editing.id
                  })
                }
              >
                History
              </button>
            ) : null
          }
          footer={
            <>
              {(createMutation.isError || mutation.isError) && (
                <span className="text-sm text-red-600 mr-auto">Error saving candidate</span>
              )}
              <button
                type="button"
                className={CANDIDATE_MODAL_FOOTER_BTN}
                onClick={embedded ? closeStaffModal : () => setShowModal(false)}
              >
                Cancel
              </button>
              <button type="button" className={CANDIDATE_MODAL_PRIMARY_BTN} onClick={submitForm} disabled={createMutation.isLoading || mutation.isLoading}>
                {editing ? 'Save changes' : 'Add candidate'}
              </button>
            </>
          }
        >
            <div className="space-y-4">
            {/* Photo and Resume Upload */}
            <div className={candidateSectionClass(0)}>
              <CandidateSectionHeader title="Photo & Resume" />
              <div className={`${CANDIDATE_FORM_GRID} items-start`}>
                <div className="min-w-0">
                  <div className="font-medium text-xs mb-2">Photo</div>
                  <div className="flex items-center gap-4">
                    {(form.photoPreview || editing?.photo || form.photo) ? (
                      <img
                        src={form.photoPreview || uploadUrl(editing?.photo || form.photo)}
                        alt="Profile"
                        className="w-20 h-20 object-cover border border-[hsl(var(--ds-border))] rounded-lg"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-[hsl(var(--ds-muted))] flex items-center justify-center text-[hsl(var(--ds-muted-fg))] text-xs rounded-lg">No Photo</div>
                    )}
                    <div>
                      <input type="file" accept="image/*" className="text-sm" onChange={e => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const preview = URL.createObjectURL(file);
                        setForm(f => ({ ...f, photoPreview: preview }));
                        if (editing) {
                          setForm(f => ({ ...f, photoFile: file, photoPreview: preview }));
                        } else {
                          setForm(f => ({ ...f, photoFile: file, photoPreview: preview }));
                        }
                      }} />
                      <div className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">Upload profile photo</div>
                    </div>
                  </div>
                </div>
                <div className="min-w-0 lg:col-span-2">
                  <div className="font-medium text-xs mb-1.5">Resume</div>
                  <CandidateResumeSection
                    resumeFile={form.resumeFile}
                    resumePreviewSrc={resumePreviewSrc}
                    savedResumeLink={editing?.resumeLink || form.resumeLink}
                    savedResumeFileName={form.resumeOriginalName || editing?.resumeOriginalName}
                    candidateName={form.name || form.personal?.name || editing?.displayName}
                    candidateId={editing?.id || form.id}
                    resumeText={resumeText}
                    onResumeFileChange={file => {
                      clearResumePreviewSrc();
                      setResumePreviewSrc(URL.createObjectURL(file));
                      setForm(f => ({
                        ...f,
                        resumeFile: file,
                        resumeOriginalName: file?.name || f.resumeOriginalName
                      }));
                    }}
                    onResumeTextChange={setResumeText}
                    onClearPreview={clearResumePreviewSrc}
                    resumePreviewOpen={resumePreviewOpen}
                    onResumePreviewOpenChange={setResumePreviewOpen}
                    onParse={parseResume}
                    parsingResume={parsingResume}
                    parseMessage={parseMessage}
                    saveLabel={editing ? 'Save changes' : 'Add candidate'}
                  />
                </div>
              </div>
            </div>

            {/* Profile Heading & Area of Expertise — 2×2 */}
            <div className={candidateSectionClass(1)}>
              <CandidateSectionHeader title="Profile Heading & Area of Expertise" />
              <div className={CANDIDATE_HEADING_EXPERTISE_GRID}>
                <div className="min-w-0 space-y-1.5">
                  <span className={CORPORATE.label}>Profile</span>
                  <input
                    className={CANDIDATE_INPUT_CLASS}
                    placeholder="E.g. HR Leader having 10 Years Experience"
                    value={form.profileHeading}
                    onChange={e =>
                      setForm(f => ({ ...f, profileHeading: normalizeFieldText(e.target.value, 'name') }))
                    }
                  />
                </div>
                <div className="min-w-0">
                  <ExpertiseAreasPicker
                    selected={form.areasOfExpertise || []}
                    onChange={areasOfExpertise => setForm(f => ({ ...f, areasOfExpertise }))}
                    catalog={expertiseAreasList || []}
                    showCountBadge
                  />
                </div>
              </div>
            </div>

            {/* Basic Info */}
            <div className={candidateSectionClass(2)}>
              <CandidateSectionHeader title="Basic Information *" />
              <div className={CANDIDATE_FORM_GRID}>
                <div>
                  <div className="field-with-label">
                    <input className={fieldClass(formErrors.name)} placeholder=" " value={form.name} onChange={e => { const v = normalizeFieldText(e.target.value, 'name'); setForm(f => ({ ...f, name: v, personal: { ...f.personal, name: v } })); }} />
                    <label>Name *</label>
                  </div>
                  <FieldHint error={formErrors.name} />
                </div>
                <div>
                  <div className="field-with-label">
                    <input className={fieldClass(formErrors.email)} type="email" placeholder=" " value={form.email} onChange={e => { const v = normalizeFieldText(e.target.value, 'email'); setForm(f => ({ ...f, email: v, contact: { ...f.contact, email: v } })); }} />
                    <label>Email *</label>
                  </div>
                  <FieldHint error={formErrors.email} />
                </div>
                <div>
                  <div className="field-with-label">
                    <input className={fieldClass(formErrors.phone)} placeholder=" " value={form.phone} onChange={e => { const v = normalizeFieldText(e.target.value, 'phone'); setForm(f => ({ ...f, phone: v, contact: { ...f.contact, phone: v } })); }} />
                    <label>Phone * (10–15 digits)</label>
                  </div>
                  <FieldHint error={formErrors.phone} />
                </div>
                {!editing && (
                  <div>
                    <div className="field-with-label">
                      <input className={fieldClass(formErrors.password)} placeholder=" " type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
                      <label>Password *</label>
                    </div>
                    <FieldHint error={formErrors.password} />
                  </div>
                )}
                <div>
                  <div className="field-with-label">
                    <input
                      className={fieldClass(formErrors.linkedin)}
                      placeholder=" "
                      value={form.contact.linkedin}
                      onChange={e =>
                        setForm(f => ({
                          ...f,
                          contact: { ...f.contact, linkedin: normalizeFieldText(e.target.value, 'url') }
                        }))
                      }
                    />
                    <label>LinkedIn (optional)</label>
                  </div>
                  <FieldHint error={formErrors.linkedin} />
                </div>
                <div>
                  <div className="field-with-label">
                    <input
                      className={fieldClass(formErrors.website)}
                      placeholder=" "
                      value={form.contact.website}
                      onChange={e =>
                        setForm(f => ({
                          ...f,
                          contact: { ...f.contact, website: normalizeFieldText(e.target.value, 'url') }
                        }))
                      }
                    />
                    <label>Website (optional)</label>
                  </div>
                  <FieldHint error={formErrors.website} />
                </div>
              </div>
            </div>

            <div className={candidateSectionClass(3)}>
              <CandidateSectionHeader
                title="Work preferences"
                hint="Optional — candidate can complete in portal"
              />
              <WorkPreferencesFields
                preferredWorkingModes={form.preferredWorkingModes}
                contactChannels={form.contactChannels}
                preferredWorkLocations={form.preferredWorkLocations}
                onToggleWorkingMode={toggleWorkingMode}
                onToggleContactChannel={toggleContactChannel}
                onLocationsChange={preferredWorkLocations =>
                  setForm(f => ({
                    ...f,
                    preferredWorkLocations,
                    location: preferredWorkLocations.join(', ')
                  }))
                }
                locationCatalog={locationCatalog}
                errors={formErrors}
              />
            </div>

            {/* Personal Details */}
            <div className={candidateSectionClass(4)}>
              <CandidateSectionHeader title="Personal Details" />
              <div className={CANDIDATE_FORM_GRID}>
                <div className="field-with-label">
                  <input className={CANDIDATE_INPUT_CLASS} type="date" placeholder=" " value={form.personal.dob} onChange={e => setForm(f => ({ ...f, personal: { ...f.personal, dob: e.target.value } }))} />
                  <label>Date of Birth</label>
                </div>
                <div className="field-with-label">
                  <select
                    className={CANDIDATE_SELECT_CLASS}
                    value={form.personal.gender || ''}
                    onChange={e => setForm(f => ({ ...f, personal: { ...f.personal, gender: e.target.value } }))}
                  >
                    {GENDER_OPTIONS.map(opt => (
                      <option key={opt.value || 'none'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <label>Gender</label>
                </div>
                <div className="field-with-label">
                  <select
                    className={CANDIDATE_SELECT_CLASS}
                    value={form.personal.maritalStatus || ''}
                    onChange={e => setForm(f => ({ ...f, personal: { ...f.personal, maritalStatus: e.target.value } }))}
                  >
                    {MARITAL_STATUS_OPTIONS.map(opt => (
                      <option key={opt.value || 'none'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <label>Marital status</label>
                </div>
                <div className="field-with-label md:col-span-2">
                  <input className={CANDIDATE_INPUT_CLASS} placeholder=" " value={form.personal.address} onChange={e => setForm(f => ({ ...f, personal: { ...f.personal, address: normalizeFieldText(e.target.value, 'text') } }))} />
                  <label>Address</label>
                </div>
                <div className="field-with-label">
                  <input className={CANDIDATE_INPUT_CLASS} placeholder=" " value={form.personal.city} onChange={e => setForm(f => ({ ...f, personal: { ...f.personal, city: normalizeFieldText(e.target.value, 'name') } }))} />
                  <label>City</label>
                </div>
                <div className="field-with-label">
                  <select
                    className={CANDIDATE_SELECT_CLASS}
                    value={form.personal.state || ''}
                    onChange={e => setForm(f => ({ ...f, personal: { ...f.personal, state: e.target.value } }))}
                  >
                    <option value="">Select state</option>
                    {INDIA_STATES_AND_UTS.map(state => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  <label>State</label>
                </div>
                <div className="field-with-label">
                  <input className={CANDIDATE_INPUT_CLASS} placeholder=" " value={form.personal.zip} onChange={e => setForm(f => ({ ...f, personal: { ...f.personal, zip: e.target.value } }))} />
                  <label>ZIP</label>
                </div>
                <div className="field-with-label">
                  <input className={`${CANDIDATE_INPUT_CLASS} bg-[hsl(var(--ds-muted))]`} placeholder=" " value={form.personal.country || 'India'} onChange={e => setForm(f => ({ ...f, personal: { ...f.personal, country: normalizeFieldText(e.target.value, 'name') || 'India' } }))} />
                  <label>Country</label>
                </div>
              </div>
            </div>

            {/* Educational Details */}
            <div className={candidateSectionClass(5)}>
              <CandidateSectionHeader
                title="Educational Details"
                actions={
                  <button type="button" className="text-xs text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))]" onClick={addEducation}>
                    + Add Education
                  </button>
                }
              />
              <div className="space-y-2">
                {(form.educational || []).map((edu, idx) => (
                  <div key={idx} className={`flex gap-2 items-start ${CORPORATE.nestedCard}`}>
                    <div className={`flex-1 ${CANDIDATE_EDUCATION_ROW_GRID}`}>
                      <div className={CANDIDATE_EDU_DEGREE_CELL}>
                        <select
                          className={CANDIDATE_SELECT_CLASS}
                          value={edu.degree || ''}
                          onChange={e => updateEducation(idx, 'degree', e.target.value)}
                        >
                          <option value="">Select degree</option>
                          {EDUCATIONAL_QUALIFICATION_OPTIONS.map(option => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <label>Degree</label>
                      </div>
                      <div className={CANDIDATE_EDU_INSTITUTION_CELL}>
                        <input className={CANDIDATE_INPUT_CLASS} placeholder=" " value={edu.institution || ''} onChange={e => updateEducation(idx, 'institution', e.target.value)} />
                        <label>Institution</label>
                      </div>
                      <div className={CANDIDATE_EDU_SMALL_CELL}>
                        <input className={CANDIDATE_INPUT_CLASS} placeholder=" " value={edu.year || ''} onChange={e => updateEducation(idx, 'year', e.target.value)} />
                        <label>Year</label>
                      </div>
                      <div className={CANDIDATE_EDU_SMALL_CELL}>
                        <input className={CANDIDATE_INPUT_CLASS} placeholder=" " value={edu.field || ''} onChange={e => updateEducation(idx, 'field', e.target.value)} />
                        <label>Field</label>
                      </div>
                      <div className={CANDIDATE_EDU_SMALL_CELL}>
                        <input className={CANDIDATE_INPUT_CLASS} placeholder=" " value={edu.gpa || ''} onChange={e => updateEducation(idx, 'gpa', e.target.value)} />
                        <label>GPA</label>
                      </div>
                    </div>
                    <button type="button" className="text-red-600 hover:text-red-700 text-sm px-2" onClick={() => removeEducation(idx)}>✕</button>
                  </div>
                ))}
                {(!form.educational || form.educational.length === 0) && (
                  <div className="text-xs text-[hsl(var(--ds-muted-fg))] text-center py-2">No education entries. Click "+ Add Education" to add one.</div>
                )}
              </div>
            </div>

            {/* Professional Details */}
            <div className={candidateSectionClass(6)}>
              <CandidateSectionHeader
                title="Professional Details"
                actions={
                  <>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer text-[hsl(var(--ds-fg))] font-normal">
                      <input
                        type="checkbox"
                        checked={!!form.isFresher}
                        onChange={e =>
                          setForm(f => ({
                            ...f,
                            isFresher: e.target.checked,
                            experience: e.target.checked
                              ? '0'
                              : String(
                                  computeExperienceYearsFromProfessional(f.professional) ??
                                    f.experience ??
                                    ''
                                )
                          }))
                        }
                      />
                      Fresher
                    </label>
                    {!form.isFresher && (
                      <button type="button" className="text-xs text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))]" onClick={addProfession}>
                        + Add Profession
                      </button>
                    )}
                  </>
                }
              />
              <FieldHint error={formErrors.experiencePath} />
              <div className="space-y-2">
                {(form.professional || []).map((prof, idx) => (
                  <div key={idx} className={`flex gap-2 items-start ${CORPORATE.nestedCard}`}>
                    <div className={`flex-1 ${CANDIDATE_FORM_ROW_GRID}`}>
                      <div className="field-with-label">
                        <input className={CANDIDATE_INPUT_CLASS} placeholder=" " value={prof.currentCompany || ''} onChange={e => updateProfession(idx, 'currentCompany', e.target.value)} />
                        <label>Company</label>
                      </div>
                      <div className="field-with-label">
                        <input className={CANDIDATE_INPUT_CLASS} placeholder=" " value={prof.designation || ''} onChange={e => updateProfession(idx, 'designation', e.target.value)} />
                        <label>Designation</label>
                      </div>
                      <div className="field-with-label">
                        <input className={CANDIDATE_INPUT_CLASS} type="date" placeholder=" " value={prof.startDate || ''} onChange={e => updateProfession(idx, 'startDate', e.target.value)} />
                        <label>Start Date</label>
                      </div>
                      <div className="field-with-label">
                        <input className={CANDIDATE_INPUT_CLASS} type="date" placeholder=" " value={prof.endDate || ''} onChange={e => updateProfession(idx, 'endDate', e.target.value)} />
                        <label>End Date</label>
                      </div>
                      <div className="field-with-label">
                        <input className={CANDIDATE_INPUT_CLASS} placeholder=" " value={prof.description || ''} onChange={e => updateProfession(idx, 'description', e.target.value)} />
                        <label>Description</label>
                      </div>
                    </div>
                    <button type="button" className="text-red-600 hover:text-red-700 text-sm px-2" onClick={() => removeProfession(idx)}>✕</button>
                  </div>
                ))}
                {(!form.professional || form.professional.length === 0) && (
                  <div className="text-xs text-[hsl(var(--ds-muted-fg))] text-center py-2">
                    {form.isFresher
                      ? 'Fresher — professional history not required.'
                      : 'No profession entries. Click "+ Add Profession" to add one.'}
                  </div>
                )}
              </div>
            </div>

            <div className={candidateSectionClass(7)}>
              <CandidateSectionHeader
                title="Core Competencies"
                hint="Search, pick from catalog, or add new core competencies"
              />
              <SkillsPicker
                selectedSkills={form.skills || []}
                onChange={skills => setForm(f => ({ ...f, skills }))}
                catalog={skillsList || []}
                onCreateSkill={handleCreateSkill}
                error={formErrors.skills}
                allowRemoveSelected={staffCanEditSkills}
                showCountBadge
              />
            </div>

            <div className={candidateSectionClass(8)}>
              <CandidateSectionHeader
                title="Technical Skills"
                hint="Tools, platforms, languages, and technologies"
              />
              <SkillsPicker
                selectedSkills={form.technicalSkills || []}
                onChange={technicalSkills => setForm(f => ({ ...f, technicalSkills }))}
                catalog={skillsList || []}
                onCreateSkill={handleCreateSkill}
                allowRemoveSelected={staffCanEditSkills}
                showCountBadge
              />
            </div>

            {/* Salary & Experience */}
            <div className={candidateSectionClass(9)}>
              <CandidateSectionHeader title="Salary & Experience" />
              <div className={CANDIDATE_SALARY_GRID}>
                <div className="min-w-0">
                  <div className="field-with-label">
                    <input
                      className={fieldClass(formErrors.currentSalary)}
                      type="text"
                      inputMode="numeric"
                      placeholder=" "
                      value={form.currentSalary}
                      onChange={e => setForm(f => ({ ...f, currentSalary: e.target.value.replace(/\D/g, '') }))}
                    />
                    <label>{SALARY_CURRENT_LABEL}</label>
                  </div>
                  <FieldHint error={formErrors.currentSalary} hint={salaryHintInr(form.currentSalary)} />
                </div>
                <div className="min-w-0">
                  <div className="field-with-label">
                    <input
                      className={fieldClass(formErrors.expectedSalary)}
                      type="text"
                      inputMode="numeric"
                      placeholder=" "
                      value={form.expectedSalary}
                      onChange={e => setForm(f => ({ ...f, expectedSalary: e.target.value.replace(/\D/g, '') }))}
                    />
                    <label>{SALARY_EXPECTED_LABEL}</label>
                  </div>
                  <FieldHint error={formErrors.expectedSalary} hint={salaryHintInr(form.expectedSalary)} />
                </div>
                <div className="min-w-0">
                  <div className="field-with-label">
                    <input
                      className={`${fieldClass(formErrors.experience)} ${form.isFresher ? 'bg-[hsl(var(--ds-muted))] cursor-not-allowed' : ''}`}
                      type="number"
                      min="0"
                      placeholder=" "
                      value={form.isFresher ? '0' : form.experience}
                      disabled={!!form.isFresher}
                      onChange={e => setForm(f => ({ ...f, experience: e.target.value }))}
                    />
                    <label>Experience (years)</label>
                  </div>
                  <FieldHint error={formErrors.experience} />
                </div>
                <div className="min-w-0">
                  <NoticePeriodField
                    value={form.availability}
                    onChange={availability => setForm(f => ({ ...f, availability }))}
                    error={formErrors.availability}
                  />
                </div>
              </div>
            </div>

            <div className={candidateSectionClass(10)}>
              <CandidateSectionHeader title="Languages Known" />
              <LanguagesKnownSection
                languagesKnown={form.languagesKnown || []}
                onChange={languagesKnown =>
                  setForm(f => ({ ...f, languagesKnown: normalizeLanguagesKnown(languagesKnown) }))
                }
              />
            </div>

            {/* Status */}
            <div className={candidateSectionClass(11)}>
              <CandidateSectionHeader title="Status" />
              <div className="field-with-label">
                <select className={CANDIDATE_SELECT_CLASS} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <label>Status</label>
              </div>
            </div>
            </div>
        </ModalShell>
      )}

      {/* Skills Modal */}
      {showSkillsModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowSkillsModal(null)}>
          <div className="bg-[hsl(var(--ds-surface))] shadow-lg p-6 w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h2 className="text-lg font-semibold">Skills</h2>
              <button className="text-[hsl(var(--ds-muted-fg))]" onClick={() => setShowSkillsModal(null)}>✕</button>
            </div>
            <div className="overflow-y-auto flex-1">
              <div className="flex flex-wrap gap-2">
                {(showSkillsModal.skills || []).length === 0 ? (
                  <span className="text-[hsl(var(--ds-muted-fg))] text-sm">No skills listed</span>
                ) : (
                  (showSkillsModal.skills || []).map(s => (
                    <span key={s} className="px-2 py-1 bg-[hsl(var(--ds-muted))] text-xs">{s}</span>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {historyCandidate && (
        <CandidateHistoryModal
          candidateId={historyCandidate.id}
          candidateName={historyCandidate.name}
          onClose={() => setHistoryCandidate(null)}
        />
      )}

      <ResumePreviewModal
        open={!!listResumePreview?.src}
        onClose={() => setListResumePreview(null)}
        src={listResumePreview?.src}
        fileName={listResumePreview?.fileName}
        candidateName={listResumePreview?.candidateName}
        candidateId={listResumePreview?.candidateId}
      />
    </div>
  );
}

